import { Injectable, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, of } from 'rxjs';
import { map, catchError, takeUntil, tap, distinctUntilChanged } from 'rxjs/operators';
import { ApiService } from './api.service';
import { AuthService } from '../../features/auth/services/auth.service';

interface LogoCache {
  logo: string | null;
  organizationId: string;
  timestamp: number;
}

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour
const ENDPOINT = '/api/Auth/GetOrganizationLogo';

@Injectable({ providedIn: 'root' })
export class CompanyLogoService implements OnDestroy {
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);

  private readonly logoSubject = new BehaviorSubject<string | null>(null);
  private readonly destroy$ = new Subject<void>();

  private cache: LogoCache | null = null;
  private fetchInProgress = false;

  /** Observable that emits the current company logo URL/base64 or null. */
  readonly logo$ = this.logoSubject.asObservable();

  constructor() {
    this.subscribeToOrganizationChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Fetch the logo, using cache if valid. */
  fetchLogo(): void {
    const user = this.authService.currentUserValue;
    const orgId = user?.organizationId ?? '';

    if (!orgId) {
      this.logoSubject.next(null);
      return;
    }

    // Return cached value if still valid for this org
    if (this.isCacheValid(orgId)) {
      this.logoSubject.next(this.cache!.logo);
      return;
    }

    // Prevent duplicate in-flight requests
    if (this.fetchInProgress) return;
    this.fetchInProgress = true;

    this.apiService
      .get<any>(ENDPOINT, { skipLoader: true })
      .pipe(
        map((response) => this.extractLogo(response)),
        tap((logo) => {
          this.cache = { logo, organizationId: orgId, timestamp: Date.now() };
          this.fetchInProgress = false;
        }),
        catchError(() => {
          this.fetchInProgress = false;
          return of(null);
        })
      )
      .subscribe((logo) => {
        this.logoSubject.next(logo);
      });
  }

  /** Invalidate cache and re-fetch. Only refetches if the edited org is the current one. */
  invalidateAndRefetch(editedOrganizationId?: number | string): void {
    if (editedOrganizationId != null) {
      const currentOrgId = this.authService.currentUserValue?.organizationId ?? '';
      if (String(editedOrganizationId) !== String(currentOrgId)) {
        return; // Not the current org — no need to refresh
      }
    }

    this.cache = null;
    this.fetchInProgress = false;
    this.fetchLogo();
  }

  /** Clear all cached data (e.g. on logout). */
  clearCache(): void {
    this.cache = null;
    this.fetchInProgress = false;
    this.logoSubject.next(null);
  }

  private isCacheValid(orgId: string): boolean {
    if (!this.cache) return false;
    if (this.cache.organizationId !== orgId) return false;
    return Date.now() - this.cache.timestamp < CACHE_DURATION;
  }

  private subscribeToOrganizationChanges(): void {
    this.authService.currentUser$
      .pipe(
        map((user) => user?.organizationId ?? ''),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((orgId) => {
        if (orgId) {
          // Org changed — invalidate cache and fetch fresh
          if (this.cache && this.cache.organizationId !== orgId) {
            this.invalidateAndRefetch();
          } else if (!this.cache) {
            this.fetchLogo();
          }
        } else {
          this.clearCache();
        }
      });
  }

  private extractLogo(response: any): string | null {
    // Handle the API response: { data: { logoUrl: '...' }, messageType: 0, message: '...' }
    const data = response?.data;
    if (!data) return null;

    const logo = data.logoUrl ?? data.LogoUrl ?? data.logo ?? data.Logo ?? null;
    if (!logo || typeof logo !== 'string' || !logo.trim()) return null;

    const trimmed = logo.trim();
    if (trimmed.startsWith('http') || trimmed.startsWith('data:')) {
      return trimmed;
    }
    // Raw base64
    return `data:image/png;base64,${trimmed}`;
  }
}
